import AppForms from "@toothfairy/shared-ui/AppForms";
import OnBoarding from "@toothfairy/shared-ui/AppOnBoardingScreen";
import AppSlider from "@toothfairy/shared-ui/AppSlider";
import { AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, TouchableOpacity, View } from "react-native";
import ToothFairySubmitButton from "../../../Components/ToothFairyComponents/ToothFairySubmitButton";
import useCredits from "../../../Hooks/useCredits";
import RechargeSheet from "../../../Components/Credits/RechargeSheet";
import Constants from "../../../Constants";
import {
  ENABLE_CONSUMPTION_BILLING,
  DEFAULT_NEW_WORKSPACE_SEATS,
  formatUsd,
  seatsToUoI,
} from "../../../Constants/billing";

const inputFieldsWidth = Platform.OS === "web" ? 340 : 250;
const dropdownWidth = Platform.OS === "web" ? 360 : 250;

// LowCreditsBanner: shown inside the wizard only once the parent choice has
// been made AND the resulting workspace will be billed through credits (i.e. a
// non-child pay-as-you-go workspace, or a child of a Stripe/consumption parent).
// A child of an enterprise parent billed via Xero does NOT need credits, so the
// banner is suppressed in that case.
const LowCreditsBanner = ({ visible = true }) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const credits = useCredits();
  const [open, setOpen] = useState(false);

  if (!visible) return null;
  if ((credits?.balanceUsd || 0) > 0) return null;

  return (
    <>
      <View
        style={{
          width: "100%",
          maxWidth: 440,
          alignSelf: "center",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: currentTheme?.theme?.softBackgroundColor,
          borderLeftWidth: 3,
          borderLeftColor: currentTheme?.theme?.orange || "#FF9800",
          borderRadius: 8,
          padding: 12,
          marginBottom: 12,
          gap: 10,
        }}
      >
        <View style={{ flex: 1, flexWrap: "wrap" }}>
          <AppText style={{ fontWeight: "bold", fontSize: 13 }}>
            {t("workspaces.wizard.noCredits.title")}
          </AppText>
          <AppText
            style={{
              fontSize: 12,
              color: currentTheme?.theme?.secondary_font_color,
              marginTop: 2,
            }}
          >
            {t("workspaces.wizard.noCredits.description")}
          </AppText>
        </View>
        <TouchableOpacity
          onPress={() => setOpen(true)}
          style={{
            paddingVertical: 8,
            paddingHorizontal: 14,
            borderRadius: 8,
            backgroundColor: currentTheme?.theme?.primary,
          }}
        >
          <AppText
            style={{ color: currentTheme?.theme?.white, fontWeight: "bold", fontSize: 12 }}
          >
            {t("workspaces.wizard.noCredits.recharge")}
          </AppText>
        </TouchableOpacity>
      </View>
      <RechargeSheet
        isModalVisible={open}
        closeModal={() => setOpen(false)}
        credits={credits}
        returnTo="/workspaces"
      />
    </>
  );
};

// WorkspaceCreationWizard: multi-step (AppSlider) creation flow for the
// consumption model. Steps: name -> main/parent -> domain & type.
//
// Billing note: credits are a single user-level pool (the Stripe customer
// balance), spent gradually as consumption happens. There is no per-workspace
// credit assignment. If the user has no credits a recharge banner is shown so
// they can top up their account before proceeding.
const WorkspaceCreationWizard = ({
  handleFormSubmit,
  eligibleParentWorkspaces = [],
  hasExistingWorkspaces = false,
  workspaceMutationInProgress = false,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const credits = useCredits();
  const AppToasty = AppToast.useToast();
  const [rechargeOpen, setRechargeOpen] = useState(false);
  // AppSlider is 1-indexed (renders items[index - 1]), so the first step is 1.
  const [sliderIndex, setSliderIndex] = useState(1);
  const [values, setValues] = useState({});

  // The main/parent step only makes sense once the user already has at least
  // one workspace to attach to. A brand-new user skips straight to the domain
  // step (their first workspace is implicitly a main workspace).
  const showParentStep = hasExistingWorkspaces;

  const parentItems = useMemo(
    () => [
      { id: "none", title: t("workspaces.wizard.parent.mainWorkspace") },
      ...eligibleParentWorkspaces.map((ws) => ({ id: ws.id, title: ws.title })),
    ],
    [eligibleParentWorkspaces, t],
  );

  const domainItems = useMemo(
    () =>
      (Constants.workspaceCreationData?.workspaceDomain?.fieldOptions || []).map(
        (u) => ({
          ...u,
          title: t(`workspaces.form.domainOptions.${u?.id}`, {
            defaultValue: u?.title,
          }),
        }),
      ),
    [t],
  );

  // Resolve the selected parent so we can decide whether this workspace needs
  // credits. A main workspace (no parent) on the consumption model needs credits;
  // a child inherits its parent's billing: enterprise + Xero = no credits needed,
  // everything else (Stripe / consumption) does.
  const selectedParentId =
    values?.parentWorkspaceID && values?.parentWorkspaceID !== "none"
      ? values.parentWorkspaceID
      : null;
  const selectedParent = selectedParentId
    ? eligibleParentWorkspaces.find((ws) => ws.id === selectedParentId)
    : null;
  // Enterprise is the non-consumption tier (billed via Xero), so a child of an
  // enterprise parent never needs the credit recharge prompt.
  const isChildOfEnterprise =
    !!selectedParent &&
    (selectedParent?.subscriptionType || "").toLowerCase() === "enterprise";

  // The banner should only appear once the parent choice has actually been
  // committed (after the parent step's form submits), not on the name step
  // where we don't yet know the billing arrangement. When the parent step is
  // skipped (no existing workspaces) the workspace is implicitly a main one, so
  // the parent choice is treated as made ("none").
  const parentChoiceMade =
    values?.parentWorkspaceID != null || !showParentStep;
  const requiresCredits =
    ENABLE_CONSUMPTION_BILLING && parentChoiceMade && !isChildOfEnterprise;

  const handleStepSubmit = (stepValues) => {
    setValues((prev) => ({ ...prev, ...stepValues }));
    setSliderIndex((prev) => prev + 1);
  };

  const handleFinalSubmit = (stepValues) => {
    const finalValues = { ...values, ...stepValues };
    const isChild =
      finalValues.parentWorkspaceID && finalValues.parentWorkspaceID !== "none";

    // Hard block: a MAIN (non-child) workspace on the consumption model draws
    // from the user's credit pool. If there are no credits, do NOT create it —
    // show an error and open the recharge sheet. EXCEPT when a recharge is
    // currently being confirmed (the user just paid on Stripe and we're waiting
    // on the synchronous confirmRecharge to land the credit) — in that case we
    // hold with a neutral toast instead of a hard block, so we don't wrongly
    // tell a paying user they can't create their workspace.
    // Child workspaces inherit their parent's billing, so they are allowed
    // regardless of the credit balance.
    if (ENABLE_CONSUMPTION_BILLING && !isChild && (credits?.balanceUsd || 0) <= 0) {
      if (credits?.confirmingRecharge) {
        AppToasty.show(
          t("workspaces.wizard.noCredits.confirmingPayment", {
            defaultValue: "Confirming your payment — try again in a moment.",
          }),
          { type: "normal" },
        );
        return;
      }
      AppToasty.show(t("workspaces.wizard.noCredits.blocked"), { type: "danger" });
      setRechargeOpen(true);
      return;
    }

    const parentForBilling = isChild
      ? eligibleParentWorkspaces.find((ws) => ws.id === finalValues.parentWorkspaceID)
      : null;
    // A child inherits its parent's billing provider and subscription type.
    const payload = {
      workspaceName: finalValues.workspaceName,
      workspaceMed: false,
      baseModel: "NONE",
      domain: finalValues.domain || "DEFAULT",
      subscriptionType: isChild
        ? parentForBilling?.subscriptionType || (ENABLE_CONSUMPTION_BILLING ? "business" : "pro")
        : ENABLE_CONSUMPTION_BILLING
        ? "business"
        : "pro",
      // handleFormSubmit converts this UoI value to seats (convertUoIToMaxUsers),
      // so send the default allocation as UoI. Child workspaces ignore it (they
      // inherit the parent's allocation).
      maxUsers: seatsToUoI(DEFAULT_NEW_WORKSPACE_SEATS, "business"),
      parentWorkspaceID: isChild ? finalValues.parentWorkspaceID : null,
      billingProvider: isChild
        ? parentForBilling?.billingProvider || "STRIPE"
        : "STRIPE",
      supportHours: 0,
      reservedComputeAllocation: false,
      logo: null,
    };
    handleFormSubmit(payload);
  };

  const nameStep = (
    <OnBoarding.OnBoardingScreen
      key="name"
      isEnabledOnFilled
      formInitialValues={{ workspaceName: values?.workspaceName || "" }}
      formValidationSchema={AppForms.yup.object().shape({
        workspaceName: AppForms.yup
          .string()
          .required()
          .label(t("workspaces.form.name")),
      })}
      ContainerActionComponent={ToothFairySubmitButton}
      icon={AppAssets.icons.workspace}
      onFormSubmit={handleStepSubmit}
      title={t("workspaces.wizard.name.title")}
      subtitle={t("workspaces.wizard.name.subtitle")}
    >
      <AppForms.AppFormField
        style={{ textAlign: "center" }}
        isMandatory
        width={inputFieldsWidth}
        name="workspaceName"
        placeholder={t("workspaces.form.workspaceNamePlaceholder")}
        autoFocus
      />
    </OnBoarding.OnBoardingScreen>
  );

  const parentStep = (
    <OnBoarding.OnBoardingScreen
      key="parent"
      isEnabledOnFilled
      formInitialValues={{
        parentWorkspaceID: values?.parentWorkspaceID || "none",
      }}
      formValidationSchema={AppForms.yup.object().shape({
        parentWorkspaceID: AppForms.yup.string().nullable(),
      })}
      ContainerActionComponent={ToothFairySubmitButton}
      icon={AppAssets.icons.genericDepartment}
      onFormSubmit={handleStepSubmit}
      title={t("workspaces.wizard.parent.title")}
      subtitle={t("workspaces.wizard.parent.subtitle")}
    >
      <AppForms.AppFormDropDown
        formFieldContainerStyle={{ flex: 1 }}
        isElevated={false}
        textAlign="center"
        dropDownStyle={{
          borderColor: currentTheme?.theme?.border_color,
          minWidth: dropdownWidth,
        }}
        name="parentWorkspaceID"
        items={parentItems}
        propertyLabel="title"
        propertyValue="id"
      />
    </OnBoarding.OnBoardingScreen>
  );

  const domainStep = (
    <OnBoarding.OnBoardingScreen
      key="domain"
      isEnabledOnFilled
      formInitialValues={{ domain: values?.domain || "DEFAULT" }}
      formValidationSchema={AppForms.yup.object().shape({
        domain: AppForms.yup
          .string()
          .required()
          .label(t("workspaces.form.businessType")),
      })}
      ContainerActionComponent={ToothFairySubmitButton}
      icon={AppAssets.icons.domain}
      onOnBoardingSubmit={handleFinalSubmit}
      isLast
      title={t("workspaces.wizard.domain.title")}
      subtitle={t("workspaces.wizard.domain.subtitle")}
    >
      <AppForms.AppFormDropDown
        formFieldContainerStyle={{ flex: 1 }}
        isElevated={false}
        textAlign="center"
        dropDownStyle={{
          borderColor: currentTheme?.theme?.border_color,
          minWidth: dropdownWidth,
        }}
        isMandatory
        name="domain"
        items={domainItems}
        propertyLabel="title"
        propertyValue="id"
      />
    </OnBoarding.OnBoardingScreen>
  );

  const steps = useMemo(() => {
    const list = [nameStep];
    if (showParentStep) list.push(parentStep);
    list.push(domainStep);
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, domainItems, parentItems, showParentStep]);

  return (
    <View style={{ width: "100%" }}>
      <LowCreditsBanner visible={requiresCredits} />
      <AppSlider
        index={sliderIndex}
        items={steps}
        onIndicatorClick={(i) => setSliderIndex(i)}
        styleSliderWrapper={{ height: "auto" }}
      />
      <RechargeSheet
        isModalVisible={rechargeOpen}
        closeModal={() => setRechargeOpen(false)}
        credits={credits}
        returnTo="/workspaces"
      />
    </View>
  );
};

export default WorkspaceCreationWizard;
